import { API_BASE_URL } from '../config/settings';
import { ClassificationReviewPanel, ClassificationData } from './ClassificationReviewPanel';

// BUG #7, #11 FIX: Interface actualizada con resourceCategory (no category) y confidence opcional
interface DebugData {
  extraction?: {
    content: string;
    author?: string;
    channel?: string;
    thumbnailUrl?: string;
    title?: string;
    description?: string;
    duration?: number;
  };
  stage1Input?: string;
  stage1Output?: {
    type: string;
    resourceCategory: string;  // BUG #11: usar resourceCategory consistentemente
    tags: string[];
    project?: string;
    title?: string;
    summary: string;
    contentFormat?: string;
    hookType?: string;
    confidence?: number;  // BUG #7: ahora viene del backend
  };
}

interface IngestResponse {
  success: boolean;
  data?: {
    id: string;
    type: 'knowledge' | 'multimedia';
    status: 'processed' | 'pending_transcription' | 'failed';
    prefilled?: boolean;
    classification?: {
      type?: string;
      tags?: string[];
      resourceCategory?: string;
      summary?: string;
      contentFormat?: string;
      hookType?: string;
      confidence?: number;
    };
    isDuplicate?: boolean;
    duplicateAction?: 'created' | 'skipped' | 'updated';
    debug?: DebugData;
  };
  error?: string;
}

export class IngestModal {
  private modalEl: HTMLElement | null = null;
  private onSuccess: (() => void) | null = null;
  private currentStep: 'ingest' | 'review' = 'ingest';
  private lastIngestResult: IngestResponse['data'] | null = null;
  private lastContent: string | null = null;
  private lastStatus: string | null = null;

  show(): void {
    this.render();
  }

  onIngestSuccess(callback: () => void): void {
    this.onSuccess = callback;
  }

  private render(): void {
    // Remove existing modal
    this.modalEl?.remove();

    const overlay = document.createElement('div');
    overlay.className = 'ingest-modal-overlay';
    overlay.innerHTML = `
      <div class="ingest-modal">
        <div class="ingest-modal-header">
          <h2>Add Content</h2>
          <button class="ingest-modal-close">
            <span class="material-symbols-outlined">close</span>
          </button>
        </div>

        <div class="ingest-modal-body">
          <div class="ingest-tabs">
            <button class="ingest-tab active" data-tab="url">From URL</button>
            <button class="ingest-tab" data-tab="content">Direct Content</button>
          </div>

          <div class="ingest-tab-content" id="ingest-url-tab">
            <div class="ingest-field">
              <label>URL</label>
              <input type="url" id="ingest-url" placeholder="https://youtube.com/watch?v=... or any URL" />
              <p class="ingest-hint">Supports: YouTube, TikTok, Instagram, Web pages, PDFs, Notion, Google Docs</p>
            </div>

            <div class="ingest-options">
              <label class="ingest-checkbox">
                <input type="checkbox" id="ingest-extract" checked />
                <span>Extract content from URL</span>
              </label>
              <label class="ingest-checkbox">
                <input type="checkbox" id="ingest-transcribe" />
                <span>Transcribe video (YouTube/TikTok/Instagram)</span>
              </label>
            </div>
          </div>

          <div class="ingest-tab-content hidden" id="ingest-content-tab">
            <div class="ingest-field">
              <label>Title</label>
              <input type="text" id="ingest-title" placeholder="Document title" />
            </div>
            <div class="ingest-field">
              <label>Content</label>
              <textarea id="ingest-content" rows="8" placeholder="Paste your content here (markdown supported)..."></textarea>
            </div>
          </div>

          <div class="ingest-divider"></div>

          <div class="ingest-options">
            <label class="ingest-checkbox">
              <input type="checkbox" id="ingest-prefill" checked />
              <span>Auto-classify with AI (tags, category, summary)</span>
            </label>
            <label class="ingest-checkbox">
              <input type="checkbox" id="ingest-debug" />
              <span>Debug mode (show extraction + Stage 1 output)</span>
            </label>
          </div>

          <div class="ingest-field">
            <label>Deduplication</label>
            <select id="ingest-dedupe">
              <option value="skip">Skip if duplicate</option>
              <option value="update">Update if duplicate</option>
              <option value="none">Always create new</option>
            </select>
          </div>

          <div id="ingest-status" class="ingest-status hidden"></div>
        </div>

        <div class="ingest-modal-footer">
          <button class="ingest-btn ingest-btn-secondary" data-action="cancel">Cancel</button>
          <button class="ingest-btn ingest-btn-primary" data-action="submit">
            <span class="material-symbols-outlined">cloud_upload</span>
            Ingest
          </button>
        </div>
      </div>

      <style>
        .ingest-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }
        .ingest-modal {
          background: var(--bg-main);
          border-radius: 12px;
          width: 100%;
          max-width: 520px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
        }
        .ingest-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border);
        }
        .ingest-modal-header h2 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }
        .ingest-modal-close {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: var(--text-secondary);
          border-radius: 4px;
        }
        .ingest-modal-close:hover {
          background: var(--bg-hover);
          color: var(--text-main);
        }
        .ingest-modal-body {
          padding: 20px;
          overflow-y: auto;
          flex: 1;
        }
        .ingest-tabs {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }
        .ingest-tab {
          padding: 8px 16px;
          border: 1px solid var(--border);
          background: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          color: var(--text-secondary);
          transition: all 0.15s;
        }
        .ingest-tab:hover {
          background: var(--bg-hover);
        }
        .ingest-tab.active {
          background: var(--primary);
          color: white;
          border-color: var(--primary);
        }
        .ingest-tab-content.hidden {
          display: none;
        }
        .ingest-field {
          margin-bottom: 16px;
        }
        .ingest-field label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 6px;
          color: var(--text-main);
        }
        .ingest-field input,
        .ingest-field textarea,
        .ingest-field select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-main);
          color: var(--text-main);
        }
        .ingest-field input:focus,
        .ingest-field textarea:focus,
        .ingest-field select:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        .ingest-field textarea {
          resize: vertical;
          min-height: 120px;
          font-family: var(--font-mono);
        }
        .ingest-hint {
          font-size: 12px;
          color: var(--text-muted);
          margin-top: 4px;
        }
        .ingest-options {
          display: flex;
          flex-direction: column;
          gap: 10px;
          margin-bottom: 16px;
        }
        .ingest-checkbox {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .ingest-checkbox input {
          width: 16px;
          height: 16px;
          cursor: pointer;
        }
        .ingest-divider {
          height: 1px;
          background: var(--border);
          margin: 16px 0;
        }
        .ingest-status {
          padding: 12px;
          border-radius: 6px;
          font-size: 13px;
          margin-top: 16px;
        }
        .ingest-status.hidden {
          display: none;
        }
        .ingest-status.loading {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary);
        }
        .ingest-status.success {
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        }
        .ingest-status.error {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        }
        .ingest-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px 20px;
          border-top: 1px solid var(--border);
        }
        .ingest-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border: none;
          transition: all 0.15s;
        }
        .ingest-btn-secondary {
          background: var(--bg-panel);
          color: var(--text-main);
        }
        .ingest-btn-secondary:hover {
          background: var(--bg-hover);
        }
        .ingest-btn-primary {
          background: var(--primary);
          color: white;
        }
        .ingest-btn-primary:hover {
          background: #1d4ed8;
        }
        .ingest-btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .ingest-btn .material-symbols-outlined {
          font-size: 18px;
        }
      </style>
    `;

    document.body.appendChild(overlay);
    this.modalEl = overlay;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    if (!this.modalEl) return;

    // Close button
    this.modalEl.querySelector('.ingest-modal-close')?.addEventListener('click', () => this.close());

    // Overlay click
    this.modalEl.addEventListener('click', (e) => {
      if (e.target === this.modalEl) this.close();
    });

    // Cancel button
    this.modalEl.querySelector('[data-action="cancel"]')?.addEventListener('click', () => this.close());

    // Submit button
    this.modalEl.querySelector('[data-action="submit"]')?.addEventListener('click', () => this.submit());

    // Tab switching
    this.modalEl.querySelectorAll('.ingest-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');
        this.switchTab(tabId!);
      });
    });

    // Auto-detect video URLs for transcribe checkbox
    const urlInput = this.modalEl.querySelector('#ingest-url') as HTMLInputElement;
    const transcribeCheckbox = this.modalEl.querySelector('#ingest-transcribe') as HTMLInputElement;

    urlInput?.addEventListener('input', () => {
      const url = urlInput.value.toLowerCase();
      const isVideo = url.includes('youtube.com') || url.includes('youtu.be') ||
                      url.includes('tiktok.com') || url.includes('instagram.com');
      if (isVideo) {
        transcribeCheckbox.checked = true;
      }
    });
  }

  private switchTab(tabId: string): void {
    if (!this.modalEl) return;

    // Update tab buttons
    this.modalEl.querySelectorAll('.ingest-tab').forEach(tab => {
      tab.classList.toggle('active', tab.getAttribute('data-tab') === tabId);
    });

    // Show/hide tab content
    this.modalEl.querySelector('#ingest-url-tab')?.classList.toggle('hidden', tabId !== 'url');
    this.modalEl.querySelector('#ingest-content-tab')?.classList.toggle('hidden', tabId !== 'content');
  }

  private async submit(): Promise<void> {
    if (!this.modalEl) return;

    const submitBtn = this.modalEl.querySelector('[data-action="submit"]') as HTMLButtonElement;
    const statusEl = this.modalEl.querySelector('#ingest-status') as HTMLElement;

    // Determine which tab is active
    const isUrlTab = this.modalEl.querySelector('#ingest-url-tab:not(.hidden)');

    let requestBody: Record<string, unknown>;

    if (isUrlTab) {
      const url = (this.modalEl.querySelector('#ingest-url') as HTMLInputElement).value.trim();
      const extract = (this.modalEl.querySelector('#ingest-extract') as HTMLInputElement).checked;
      const transcribe = (this.modalEl.querySelector('#ingest-transcribe') as HTMLInputElement).checked;

      if (!url) {
        this.showStatus('error', 'Please enter a URL');
        return;
      }

      requestBody = {
        sourceUrl: url,
        extract,
        transcribe
      };
    } else {
      const title = (this.modalEl.querySelector('#ingest-title') as HTMLInputElement).value.trim();
      const content = (this.modalEl.querySelector('#ingest-content') as HTMLTextAreaElement).value.trim();

      if (!content || content.length < 10) {
        this.showStatus('error', 'Content must be at least 10 characters');
        return;
      }

      requestBody = {
        title: title || 'Untitled',
        content
      };
    }

    // Common options
    requestBody.prefill = (this.modalEl.querySelector('#ingest-prefill') as HTMLInputElement).checked;
    requestBody.dedupe = (this.modalEl.querySelector('#ingest-dedupe') as HTMLSelectElement).value;
    requestBody.debug = (this.modalEl.querySelector('#ingest-debug') as HTMLInputElement).checked;
    const categoryValue = (this.modalEl.querySelector('#ingest-category') as HTMLSelectElement)?.value;
    if (categoryValue) {
      requestBody.resourceCategory = categoryValue;
    }

    // Disable submit button
    submitBtn.disabled = true;
    this.showStatus('loading', 'Processing...');

    try {
      const response = await fetch(`${API_BASE_URL}/ingest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result: IngestResponse = await response.json();

      if (result.success && result.data) {
        const { status, isDuplicate, duplicateAction } = result.data;

        // Handle duplicates - just show message and close
        if (isDuplicate) {
          const message = duplicateAction === 'skipped' ? 'Duplicate found - skipped' : 'Duplicate found - updated';
          this.showStatus('success', message);
          setTimeout(() => {
            this.close();
            this.onSuccess?.();
          }, 2000);
          return;
        }

        // ALWAYS show review step for new resources
        this.lastIngestResult = result.data;
        this.lastContent = requestBody.content as string || null;
        this.lastStatus = status; // Track if pending for async processing

        // Show debug panel if debug data is available
        if (result.data.debug) {
          this.showDebugStep(result.data.debug);
        } else {
          this.showReviewStep();
        }
      } else {
        this.showStatus('error', result.error || 'Failed to ingest content');
        submitBtn.disabled = false;
      }
    } catch (error) {
      this.showStatus('error', error instanceof Error ? error.message : 'Network error');
      submitBtn.disabled = false;
    }
  }

  private showStatus(type: 'loading' | 'success' | 'error', message: string): void {
    const statusEl = this.modalEl?.querySelector('#ingest-status') as HTMLElement;
    if (!statusEl) return;

    statusEl.className = `ingest-status ${type}`;
    statusEl.textContent = message;
  }

  close(): void {
    this.modalEl?.remove();
    this.modalEl = null;
    this.currentStep = 'ingest';
    this.lastIngestResult = null;
    this.lastContent = null;
    this.lastStatus = null;
  }

  private showReviewStep(): void {
    if (!this.modalEl || !this.lastIngestResult) return;

    this.currentStep = 'review';

    // BUG #8 FIX: Usar stage1Output cuando viene de debug, sino usar classification
    const debugOutput = this.lastIngestResult.debug?.stage1Output;
    const apiClassification = this.lastIngestResult.classification || {};

    // Mapear stage1Output a formato de classification si existe
    const classification = debugOutput ? {
      type: debugOutput.type,
      resourceCategory: debugOutput.resourceCategory,
      tags: debugOutput.tags,
      summary: debugOutput.summary,
      contentFormat: debugOutput.contentFormat,
      hookType: debugOutput.hookType,
      confidence: debugOutput.confidence
    } : apiClassification;

    const isPending = this.lastStatus === 'pending';

    // Update modal header
    const header = this.modalEl.querySelector('.ingest-modal-header h2');
    if (header) {
      header.textContent = isPending ? 'Recurso Creado (Procesando...)' : 'Revisar Clasificación';
    }

    // Replace modal body with review panel
    const body = this.modalEl.querySelector('.ingest-modal-body');
    if (!body) return;

    // Show pending message if async processing
    const pendingMessage = isPending ? `
      <div style="padding: 12px; margin-bottom: 16px; background: rgba(37, 99, 235, 0.1); border-radius: 6px; color: var(--primary); font-size: 13px;">
        <strong>ID:</strong> ${this.lastIngestResult.id}<br>
        El contenido se está extrayendo/transcribiendo. La clasificación se completará cuando esté listo.
      </div>
    ` : '';

    body.innerHTML = `${pendingMessage}<div id="classification-review-container"></div>`;

    const container = body.querySelector('#classification-review-container') as HTMLElement;

    const classificationData: ClassificationData = {
      id: this.lastIngestResult.id,
      type: classification.type || 'Resource',
      resourceCategory: classification.resourceCategory || 'Uncategorized',
      tags: classification.tags || [],
      summary: classification.summary || '',
      contentFormat: classification.contentFormat,
      hookType: classification.hookType,
      confidence: classification.confidence || 0,
      content: this.lastContent || undefined
    };

    const reviewPanel = new ClassificationReviewPanel(container, classificationData, {
      showContentPreview: !!this.lastContent,
      onAccept: () => {
        this.close();
        this.onSuccess?.();
      },
      onCancel: () => {
        this.close();
      },
      onSave: () => {
        this.close();
        this.onSuccess?.();
      }
    });

    reviewPanel.render();

    // Hide footer (panel has its own buttons)
    const footer = this.modalEl.querySelector('.ingest-modal-footer');
    if (footer) (footer as HTMLElement).style.display = 'none';
  }

  private showDebugStep(debug: DebugData): void {
    if (!this.modalEl) return;

    this.currentStep = 'review';

    // Update modal header
    const header = this.modalEl.querySelector('.ingest-modal-header h2');
    if (header) {
      header.textContent = 'Debug: Extracción + Stage 1';
    }

    // Replace modal body with debug panel
    const body = this.modalEl.querySelector('.ingest-modal-body');
    if (!body) return;

    const extraction = debug.extraction;
    const stage1Output = debug.stage1Output;

    body.innerHTML = `
      <div class="debug-panel" style="max-height: 60vh; overflow-y: auto;">
        <div class="debug-section" style="margin-bottom: 16px;">
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: var(--primary);">
            Extracción
          </h3>
          <div style="background: var(--bg-secondary); border-radius: 6px; padding: 12px; font-size: 12px;">
            <div style="margin-bottom: 8px;"><strong>Title:</strong> ${extraction?.title || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Author:</strong> ${extraction?.author || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Channel:</strong> ${extraction?.channel || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Description:</strong> ${extraction?.description || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Duration:</strong> ${extraction?.duration ? extraction.duration + 's' : 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Thumbnail:</strong> ${extraction?.thumbnailUrl ? `<img src="${extraction.thumbnailUrl}" style="max-width: 200px; border-radius: 4px;">` : 'N/A'}</div>
            <details style="margin-top: 8px;">
              <summary style="cursor: pointer; font-weight: 600;">Content (${extraction?.content?.length || 0} chars)</summary>
              <pre style="white-space: pre-wrap; word-break: break-word; max-height: 200px; overflow-y: auto; background: var(--bg-primary); padding: 8px; border-radius: 4px; margin-top: 8px; font-size: 11px;">${extraction?.content?.slice(0, 5000) || 'N/A'}</pre>
            </details>
          </div>
        </div>

        <div class="debug-section" style="margin-bottom: 16px;">
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: var(--primary);">
            Stage 1 Input
          </h3>
          <div style="background: var(--bg-secondary); border-radius: 6px; padding: 12px; font-size: 12px;">
            <details>
              <summary style="cursor: pointer; font-weight: 600;">Texto enviado al AI (${debug.stage1Input?.length || 0} chars)</summary>
              <pre style="white-space: pre-wrap; word-break: break-word; max-height: 200px; overflow-y: auto; background: var(--bg-primary); padding: 8px; border-radius: 4px; margin-top: 8px; font-size: 11px;">${debug.stage1Input?.slice(0, 3000) || 'N/A'}</pre>
            </details>
          </div>
        </div>

        <div class="debug-section" style="margin-bottom: 16px;">
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 8px; color: var(--primary);">
            Stage 1 Output
          </h3>
          <div style="background: var(--bg-secondary); border-radius: 6px; padding: 12px; font-size: 12px;">
            <div style="margin-bottom: 8px;"><strong>Type:</strong> ${stage1Output?.type || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Category:</strong> ${stage1Output?.resourceCategory || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Tags:</strong> ${stage1Output?.tags?.join(', ') || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Project:</strong> ${stage1Output?.project || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Content Format:</strong> ${stage1Output?.contentFormat || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Hook Type:</strong> ${stage1Output?.hookType || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Summary:</strong> ${stage1Output?.summary || 'N/A'}</div>
            <div style="margin-bottom: 8px;"><strong>Confidence:</strong> ${stage1Output?.confidence !== undefined ? Math.round(stage1Output.confidence * 100) + '%' : 'N/A'}</div>
          </div>
        </div>

        <div style="padding: 12px; background: rgba(37, 99, 235, 0.1); border-radius: 6px; color: var(--primary); font-size: 13px;">
          <strong>ID:</strong> ${this.lastIngestResult?.id || 'N/A'}
        </div>
      </div>
    `;

    // Update footer with close button
    const footer = this.modalEl.querySelector('.ingest-modal-footer');
    if (footer) {
      footer.innerHTML = `
        <button class="ingest-btn ingest-btn-secondary" data-action="cancel">Cerrar</button>
        <button class="ingest-btn ingest-btn-primary" data-action="review">Ver Review Panel</button>
      `;

      // Add event listener for review button
      const reviewBtn = footer.querySelector('[data-action="review"]');
      reviewBtn?.addEventListener('click', () => {
        this.showReviewStep();
      });
    }
  }
}
